import { BaseModel, BaseModelArray } from '@eview/core/base/base-model';

export interface FormAttribute extends BaseModel {
  id: number;
  key?: string;
  label: string;
  instructions?: string;
  input: FormAttributeInput;
  type: FormAttributeType;
  required: boolean;
  default?: string | number;
  priority: number;
  options?: any[];
  cardinality?: number;
  config?: FormAttributeConfig[];
  form_stage_id: number;
  response_private?: boolean;
  label_am?: string;
  label_fr?: string;
  language?: FormAttributeLanguage[];
  success?: boolean;
  post_ids?: any;
}

export interface FormAttributes extends BaseModelArray<FormAttribute> {}

export enum FormAttributeInput {
  Location = 'location',
  Text = 'text',
  Tags = 'tags',
  TextArea = 'textarea',
  Number = 'number',
  Date = 'date',
  DateTime = 'datetime',
  Select = 'select',
  Radio = 'radio',
  Checkbox = 'checkbox',
  Video = 'video',
  Upload = 'upload',
  Target = 'target',
  Perpetrators = 'perpetrator',
  Status = 'status',
  Political = 'political-cluster',
  Security = 'security-cluster',
  Natural = 'natural-cluster',
  Economic = 'economic-cluster',
  Socio = 'socio-cluster',
  Label = 'label',
  TableNumbers = 'table-numbers',
  /** Repeatable population/range rows (JSON stored as text). */
  Rows = 'rows'
}

export enum FormAttributeType {
  Point = 'point',
  Title = 'title',
  Description = 'description',
  Tags = 'tags',
  // 'Content' is an alias for 'Description'.
  Content = 'description',
  Varchar = 'varchar',
  Text = 'text',
  Decimal = 'decimal',
  Int = 'int',
  DateTime = 'datetime',
  Media = 'media',
  Radio = 'radio',
  // Custom entries, needed to handle common use cases.
  Checkbox = 'checkbox',
  Label = 'label',
  TableNumbers = 'table-numbers'
}

export const FormAttributeConfigs: {
  input: FormAttributeInput;
  type: FormAttributeType;
  text: string;
}[] = [
  {
    input: FormAttributeInput.Text,
    type: FormAttributeType.Title,
    text: 'POST_ATTRIBUTE_TYPE_TITLE'
  },
  {
    input: FormAttributeInput.Text,
    type: FormAttributeType.Description,
    text: 'POST_ATTRIBUTE_TYPE_DESCRIPTION'
  },
  {
    input: FormAttributeInput.Text,
    type: FormAttributeType.Varchar,
    text: 'POST_ATTRIBUTE_TYPE_SHORT_TEXT'
  },
  {
    input: FormAttributeInput.TextArea,
    type: FormAttributeType.Text,
    text: 'POST_ATTRIBUTE_TYPE_LONG_TEXT'
  },
  {
    input: FormAttributeInput.Rows,
    type: FormAttributeType.Text,
    text: 'POST_ATTRIBUTE_TYPE_REPEATABLE_ROWS'
  },
  {
    input: FormAttributeInput.Number,
    type: FormAttributeType.Int,
    text: 'POST_ATTRIBUTE_TYPE_NUMBER_INTEGER'
  },
  {
    input: FormAttributeInput.Number,
    type: FormAttributeType.Decimal,
    text: 'POST_ATTRIBUTE_TYPE_NUMBER_DECIMAL'
  },
  {
    input: FormAttributeInput.Location,
    type: FormAttributeType.Point,
    text: 'POST_ATTRIBUTE_TYPE_LOCATION'
  },
  {
    input: FormAttributeInput.Date,
    type: FormAttributeType.DateTime,
    text: 'POST_ATTRIBUTE_TYPE_DATE'
  },
  {
    input: FormAttributeInput.DateTime,
    type: FormAttributeType.DateTime,
    text: 'POST_ATTRIBUTE_TYPE_DATETIME'
  },
  {
    input: FormAttributeInput.Select,
    type: FormAttributeType.Varchar,
    text: 'POST_ATTRIBUTE_TYPE_SELECT'
  },
  {
    input: FormAttributeInput.Radio,
    type: FormAttributeType.Varchar,
    text: 'POST_ATTRIBUTE_TYPE_RADIOBUTTONS'
  },
  {
    input: FormAttributeInput.Checkbox,
    type: FormAttributeType.Varchar,
    text: 'POST_ATTRIBUTE_TYPE_CHECKBOXES'
  },
  {
    input: FormAttributeInput.Upload,
    type: FormAttributeType.Media,
    text: 'POST_ATTRIBUTE_TYPE_MEDIA'
  },
  {
    input: FormAttributeInput.Video,
    type: FormAttributeType.Varchar,
    text: 'POST_ATTRIBUTE_TYPE_URL'
  },
  {
    input: FormAttributeInput.Tags,
    type: FormAttributeType.Tags,
    text: 'POST_ATTRIBUTE_TYPE_TAGS'
  },
  {
    input: FormAttributeInput.Target,
    type: FormAttributeType.Tags,
    text: 'POST_ATTRIBUTE_TYPE_TARGET'
  },
  {
    input: FormAttributeInput.Perpetrators,
    type: FormAttributeType.Tags,
    text: 'POST_ATTRIBUTE_TYPE_PERPETRATORS'
  },
  {
    input: FormAttributeInput.Status,
    type: FormAttributeType.Tags,
    text: 'POST_ATTRIBUTE_TYPE_STATUS'
  },
  {
    input: FormAttributeInput.Political,
    type: FormAttributeType.Tags,
    text: 'POLITICAL_CLUSTER'
  },
  {
    input: FormAttributeInput.Economic,
    type: FormAttributeType.Tags,
    text: 'ECONOMIC_CLUSTER'
  },
  {
    input: FormAttributeInput.Socio,
    type: FormAttributeType.Tags,
    text: 'SOCIAL_CLUSTER'
  },
  {
    input: FormAttributeInput.Natural,
    type: FormAttributeType.Tags,
    text: 'NATURAL_CLUSTER'
  },
  {
    input: FormAttributeInput.Security,
    type: FormAttributeType.Tags,
    text: 'SECURITY_CLUSTER'
  },
  {
    input: FormAttributeInput.Label,
    type: FormAttributeType.Label,
    text: 'POST_ATTRIBUTE_TYPE_Label'
  }
];

/** Relation token for filter_by_parent (also stored in target.value for save compatibility). */
export const FORM_ATTR_RELATION_PARENT_ID = 'parent_id';

export interface FormAttributeConfig {
  id: number;
  action: FormAttributeConfigAction | string;
  /** Required for show / filter_by_parent; optional for rows_catalog. */
  target?: FormAttributeConfigTarget;
  /** Optional; defaults to parent_id when action is filter_by_parent. */
  relation?: string;
  /** Repeatable rows catalog when action is rows_catalog. */
  catalog?: any;
}

export enum FormAttributeConfigAction {
  Show = 'show',
  /** Child tags field: options filtered by selected parent tag via tags.parent_id. */
  FilterByParent = 'filter_by_parent',
  /** Populations/ranges catalog for repeatable JSON rows fields. */
  RowsCatalog = 'rows_catalog'
}

export const FormAttributeConfigActions: {
  action: FormAttributeConfigAction;
  text: string;
}[] = [
  {
    action: FormAttributeConfigAction.Show,
    text: 'POST_ATTRIBUTE_ACTION_SHOW'
  },
  {
    action: FormAttributeConfigAction.FilterByParent,
    text: 'POST_ATTRIBUTE_ACTION_FILTER_BY_PARENT'
  }
];

export interface FormAttributeConfigTarget {
  key: string;
  value: any;
}

/** First filter_by_parent rule on a field config, if any. */
export function getFilterByParentRule(
  config?: FormAttributeConfig[] | null
): FormAttributeConfig | null {
  if (!config?.length) {
    return null;
  }
  for (let i = 0; i < config.length; i++) {
    const rule = config[i];
    if (
      rule?.action === FormAttributeConfigAction.FilterByParent &&
      rule.target?.key
    ) {
      return rule;
    }
  }
  return null;
}

export function isFilterByParentAction(
  action: FormAttributeConfigAction | string | null | undefined
): boolean {
  return action === FormAttributeConfigAction.FilterByParent;
}

export function isRowsCatalogAction(
  action: FormAttributeConfigAction | string | null | undefined
): boolean {
  return action === FormAttributeConfigAction.RowsCatalog;
}

/** Rules editable in the attribute admin UI (show / filter_by_parent). */
export function isConditionalRuleConfig(
  config: FormAttributeConfig | null | undefined
): boolean {
  return !!config?.action && !isRowsCatalogAction(config.action);
}

export interface FormAttributeLanguage {
  id: number;
  action: string;
  text: string;
}

